import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MapPin, Layers, Ruler, Sparkles, ArrowLeft } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { priceMain, priceBox, priceActual, discountPct, stockUnit, isTile } from "@/lib/format";
import { useCities } from "@/context/CityContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { visitor } = useCities();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    api.get(`/marketplace/products/${id}`).then(({ data }) => setP(data)).catch(() => setErr(true));
  }, [id]);

  const whatsapp = async () => {
    if (!agreed) { toast.error("Please accept the Limitation of Liability to contact the seller"); return; }
    await api.post(`/marketplace/products/${id}/whatsapp-click`, { visitor_mobile: visitor || null }).catch(() => {});
    const num = (p.seller_whatsapp || "").replace(/\D/g, "");
    const msg = encodeURIComponent(`Hi, I'm interested in your tile stock on Cleartile.in:\n\n${p.title}\nSize: ${p.size} | Surface: ${p.surface}\nRate: ${priceMain(p)}${priceBox(p) ? ` (${priceBox(p)})` : ""}\nLocation: ${p.stock_location}\n\nIs this stock still available?`);
    const wa = num.length === 10 ? `91${num}` : num;
    window.open(`https://wa.me/${wa}?text=${msg}`, "_blank");
  };

  if (err) return <div className="min-h-screen grid place-items-center text-muted-foreground">Product not found.</div>;
  if (!p) return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <button onClick={() => nav(-1)} className="flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground mb-4" data-testid="back-btn">
          <ArrowLeft className="h-4 w-4" /> Back to stock
        </button>

        <div className="grid lg:grid-cols-2 gap-8">
          <div>
            <Tabs defaultValue="catalogue">
              <TabsList className="grid grid-cols-3 w-full">
                <TabsTrigger value="catalogue" data-testid="tab-catalogue">Catalogue</TabsTrigger>
                <TabsTrigger value="stock" data-testid="tab-stock-image">Original Stock</TabsTrigger>
                <TabsTrigger value="video" data-testid="tab-stock-video">Real Video</TabsTrigger>
              </TabsList>
              <TabsContent value="catalogue">
                <img src={fileUrl(p.catalogue_image)} alt="catalogue" className="w-full aspect-square object-cover rounded-2xl border" />
              </TabsContent>
              <TabsContent value="stock">
                <img src={fileUrl(p.stock_image)} alt="stock" className="w-full aspect-square object-cover rounded-2xl border" />
                <p className="text-xs text-muted-foreground mt-2 font-medium">📷 Actual photograph of the physical stock available with the seller.</p>
              </TabsContent>
              <TabsContent value="video">
                <video src={fileUrl(p.stock_video)} controls className="w-full aspect-square object-cover rounded-2xl border bg-black" data-testid="stock-video-player" />
                <p className="text-xs text-muted-foreground mt-2 font-medium">🎥 Real video showing the physical stock.</p>
              </TabsContent>
            </Tabs>
          </div>

          <div>
            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 font-bold mb-2">Clearance Stock</Badge>
            <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight leading-tight">{p.title}</h1>

            <div className="mt-4 flex items-baseline gap-2 flex-wrap">
              <span className="text-primary font-heading font-extrabold text-3xl">{priceMain(p)}</span>
              {priceActual(p) && <span className="text-lg text-muted-foreground line-through">{priceActual(p)}</span>}
              {discountPct(p) != null && <Badge data-testid="discount-badge" className="bg-rose-600 text-white border-0 font-extrabold">{discountPct(p)}% OFF</Badge>}
            </div>
            {isTile(p) && (priceBox(p) || p.pc_price) && <div className="text-sm font-semibold text-muted-foreground mt-1">{priceBox(p)}{p.pc_price ? ` · ₹${Number(p.pc_price).toLocaleString("en-IN")} / piece` : ""}</div>}
            {p.description && <p className="mt-4 text-sm text-muted-foreground leading-relaxed" data-testid="product-description-view">{p.description}</p>}

            <div className="mt-6 grid grid-cols-2 gap-3">
              <Spec icon={Ruler} label="Size" value={p.size} />
              <Spec icon={Sparkles} label="Surface" value={p.surface} />
              {p.tile_type && <Spec icon={Layers} label="Tile Type" value={p.tile_type} />}
              {p.packing_per_box && <Spec icon={Ruler} label="Packing / Box" value={p.packing_per_box} />}
              <Spec icon={Layers} label="Availability" value={`${p.stock_qty} ${stockUnit(p)}`} />
              <Spec icon={MapPin} label="Stock Location" value={p.stock_location} />
            </div>

            <div className="mt-8 sticky top-20">
              <div data-testid="liability-box" className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4 mb-3">
                <div className="text-[11px] uppercase tracking-widest font-extrabold text-amber-700 mb-2">Limitation of Liability</div>
                <p className="text-xs text-amber-900/80 leading-relaxed">
                  Cleartile.in shall not be responsible or liable for any fraud, misrepresentation, breach of contract, non-payment, non-delivery, counterfeit or unauthorised goods, or any other dispute, loss, or claim arising directly from or in connection with a transaction between a Buyer and a Seller.
                </p>
                <p className="text-xs text-amber-900/80 leading-relaxed mt-2">
                  The Buyer and Seller shall be solely responsible for conducting appropriate due diligence, verifying the identity and credentials of the other party, confirming the authenticity and condition of goods, and ensuring the accuracy and legality of the transaction. Any dispute arising between the Buyer and Seller shall be resolved directly between the concerned parties.
                </p>
                <label htmlFor="liability-accept" className="flex items-start gap-2.5 mt-3 cursor-pointer">
                  <Checkbox id="liability-accept" data-testid="liability-checkbox" checked={agreed} onCheckedChange={(v) => setAgreed(v === true)} className="mt-0.5 border-amber-500 data-[state=checked]:bg-amber-600 data-[state=checked]:border-amber-600" />
                  <span className="text-xs font-bold text-amber-900">I have read and accept the Limitation of Liability above.</span>
                </label>
              </div>
              <Button
                data-testid="whatsapp-seller-btn"
                onClick={whatsapp}
                className={`w-full h-14 text-base font-extrabold rounded-xl bg-[#25D366] hover:bg-[#1EBE5D] text-white gap-2 transition-opacity ${agreed ? "" : "opacity-60"}`}
              >
                <WhatsAppIcon /> WHATSAPP SELLER
              </Button>
              <p data-testid="buyer-disclaimer" className="text-center text-xs text-muted-foreground mt-2 font-medium">Cleartile.in is a marketplace connecting buyer and seller and it does not guarantee anything else.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Spec({ icon: Icon, label, value }) {
  return (
    <div className="bg-card border rounded-xl p-3">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-semibold uppercase tracking-wide">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className="mt-1 font-heading font-bold text-sm">{value}</div>
    </div>
  );
}

function WhatsAppIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
  );
}
